import type { AllowSizeOption } from 'components/Uploader';
import { PicProofEnum } from 'types/common';
import { IdTypeEnum } from 'types/customer';
import { printf } from 'utils/i18n';
import { LabelFieldEnum, LabelFieldsItemType } from './type';

const commonIdCardDesc: Pick<LabelFieldsItemType, 'desc'>['desc'] = {
    texts: [
        __('请确保身份证显示清晰无反光。'),
        __('请确保身份证未过期且姓名与账户持有人一致。'),
        __('请只拍摄身份证原件。TradeUP将对您的身份信息进行加密，仅用于开户和KYC目的。'),
        __('如果点击上传后没有任何反应，请确认您已授权您的设备访问相机/相册。')
    ],
    needOrderNum: true
};

/**
 * @description 目前仅支持身份证， 后续增加通用证件类型需要拓展此配置项，否则使用extraLabelFields控制即可。
 */
export const defaultLabelFields: Partial<Record<LabelFieldEnum, LabelFieldsItemType>> = {
    [LabelFieldEnum.ID_CARD]: {
        label: __('上传身份证（正反面）'),
        desc: commonIdCardDesc,
        fields: [PicProofEnum.PIC_ID_CARD, PicProofEnum.PIC_ID_OTHER],
        types: [PicProofEnum.PIC_ID_CARD, PicProofEnum.PIC_ID_OTHER],
        tooltips: [__('请上传身份证人像面'), __('请上传身份证国徽面')]
    },
    [LabelFieldEnum.NATIONAL_ID_CARD]: {
        label: __('身份证件上传'),
        desc: commonIdCardDesc,
        fields: [PicProofEnum.PIC_ID_CARD, PicProofEnum.PIC_ID_OTHER],
        types: ['pic_id_card_national', 'pic_id_other_national'],
        tooltips: [__('请上传证件正面'), __('请上传证件反面')]
    },
    [LabelFieldEnum.COMMON_ID_CARD]: {
        label: __('身份证件上传'),
        desc: commonIdCardDesc,
        fields: [PicProofEnum.PIC_ID_CARD, PicProofEnum.PIC_ID_OTHER],
        types: ['pic_common_id_card', 'pic_common_id_other'],
        tooltips: [__('请上传证件正面'), __('请上传证件反面')]
    },
    [LabelFieldEnum.PIC_PASSPORT]: {
        label: __('护照上传'),
        fields: [PicProofEnum.PIC_ID_CARD],
        types: ['pic_passport'],
        tooltips: [__('请上传护照')]
    },
    [LabelFieldEnum.PIC_EXEMPTION]: {
        label: printf(__('上传%s'), __('证明文件').toLowerCase()),
        fields: [PicProofEnum.PIC_EXEMPTION],
        types: ['commonPicAndPDF'],
        tooltips: [printf(__('上传%s'), __('证明文件').toLowerCase())]
    }
};

export const IDFields = [
    LabelFieldEnum.ID_CARD,
    LabelFieldEnum.ID_CARD2,
    LabelFieldEnum.PIC_PASSPORT,
    LabelFieldEnum.NATIONAL_ID_CARD,
    LabelFieldEnum.COMMON_ID_CARD
];

// 获取files对应的证明fields
export function getFieldNameOfFiles(files: LabelFieldEnum[]): PicProofEnum[] {
    return files
        .map(name => {
            return defaultLabelFields[name]?.fields || [];
        })
        .flat();
}

/**
 * @description 获取当前用户需要上传的代表身份证明的UploadFiles数组
 * 返回值可以直接作为UploadID组件的uploadFiles属性
 * @param idType 用户的证件类型
 */
export function getIdcardUploadFiles(idType?: IdTypeEnum): LabelFieldEnum[] {
    if (idType === IdTypeEnum.PASSPORT_IDS) {
        return [LabelFieldEnum.PIC_PASSPORT];
    }

    if (idType === IdTypeEnum.NATIONAL_IDS) {
        return [LabelFieldEnum.NATIONAL_ID_CARD];
    }

    if (idType && [IdTypeEnum.RESIDENCE_PERMIT_IDS, IdTypeEnum.EMPLOYMENT_PASS_IDS].includes(idType)) {
        return [LabelFieldEnum.COMMON_ID_CARD];
    }

    return [LabelFieldEnum.ID_CARD];
}

/**
 * @description 根据身份证件类型, 获取该身份证件对应的身份证明的字段
 * @param {IdTypeEnum} idType
 * @returns 例如: ['pic_id_card', 'pic_id_other']
 */
export function getUploadFieldsByIdType(idType: IdTypeEnum) {
    return getFieldNameOfFiles(getIdcardUploadFiles(idType));
}

export const ocrAllowSizeOption: AllowSizeOption = {
    min: 32 * 1024, // 32KB
    max: 10 * 1024 * 1024, // 10MB
    tip: printf(__('The image size should be between %s and %s'), '32KB', '10MB')
};
